import React from 'react';
import { useIntl, type IntlShape } from 'react-intl';

import b_ from 'b_';
import cx from 'classnames';
import { Text } from 'platform-components';

import { getIsCollegeTeamUtil } from '../../../common.components/user/utils';

import { getTrimmedName } from '../../get-trimmed-name';
import { getTeamLabel, type Params as UseTeamLabelParams } from '../use-team-label';
import './use-team-name.scss';

interface Params extends UseTeamLabelParams {
    /** Грейд класса */
    grade: number | null;

    /** Буква класса или название группы */
    name: string;

    /** Сократить название класса */
    shortName?: boolean;

    /** Получить не только текст, но jsx со стилями */
    jsx?: boolean;

    /** В jsx выделить жирным грейд и букву школьного класса */
    jsxBoldGrade?: boolean;

    /** Не показывать слово "класс" или "группа" */
    hideLabel?: boolean;

    /** Не показывать слово "группа" для СПО */
    hideCollegeLabel?: boolean;

    e2eIds?: {
        grade?: string;
        name?: string;
    };

    className?: string;
}

/** Отдает названием класса с учетом грейда и СПО */
export const useTeamName = (params: Params) => {
    const intl = useIntl();

    return getTeamName({ intl, ...params });
};

const b = b_.with('use-team-name');

/** Отдает названием класса с учетом грейда и СПО */
// eslint-disable-next-line complexity
export function getTeamName({
    grade,
    name,
    shortName,
    jsx,
    jsxBoldGrade,
    hideLabel,
    hideCollegeLabel,
    teamLabelCase,
    capitalizeTeamLabel,
    className,
    e2eIds,
    intl,
}: Params & { intl: IntlShape }) {
    const isCollegeTeam = getIsCollegeTeamUtil(grade);

    const teamName = shortName ? getTrimmedName(name) : name;

    if (isCollegeTeam) {
        if (hideLabel || hideCollegeLabel) {
            return jsx ? <span className={b('college-name')}>{teamName}</span> : teamName;
        }

        const teamLabel = getTeamLabel({ grade, teamLabelCase, capitalizeTeamLabel, intl });

        return jsx ? (
            <span className={b('college-name')}>
                {teamLabel} {teamName}
            </span>
        ) : (
            `${teamLabel} ${teamName}`
        );
    }

    const teamLabel = hideLabel ? '' : getTeamLabel({ grade, teamLabelCase, capitalizeTeamLabel, intl });

    if (!jsx) {
        return `${grade} «${teamName}» ${teamLabel}`;
    }

    return (
        <div className={cx(b('school'), className)}>
            <Text className={cx(b('school-grade', { bold: jsxBoldGrade }), e2eIds?.grade)}>{grade}</Text>

            <span className={cx(b('school-name-wrapper'), e2eIds?.name)}>
                «<Text className={b('school-name', { bold: jsxBoldGrade })}>{teamName}</Text>»
            </span>

            <Text className={b('school-label')}>{teamLabel}</Text>
        </div>
    );
}
